/**
 * Stream unified frontend logger.
 *
 * Pipes every log line through tauri-plugin-log so frontend output ends up in
 * the same session.log file as Rust backend output. Also installs:
 *   - console.{log,info,warn,error,debug} interceptors that forward to the plugin
 *   - window.onerror handler for uncaught synchronous errors
 *   - unhandledrejection handler for uncaught promise rejections
 *
 * Install via `installLogger()` exactly once from src/main.tsx, before render.
 *
 * Direct use: import { logger } from '@/lib/logger' and call logger.info(...)
 * etc. when you want explicit logging that does not go through console.
 */

import { IS_TAURI } from './platform'

type LogFn = (message: string) => Promise<void>

/** The five levels tauri-plugin-log exposes, in the order used below. */
type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error'

/**
 * Resolve the log sink for the current platform (Task 4093).
 *
 * Desktop pipes through `@tauri-apps/plugin-log` so frontend lines land in the
 * same `session.log` as Rust output — the reason this module exists. The browser
 * has no such file and no plugin, so it is a no-op sink: the console
 * interceptors below still call the *native* console methods first, so nothing
 * is lost from devtools; only the file mirror goes away.
 *
 * The import is dynamic and behind `IS_TAURI` so Rollup drops `plugin-log` from
 * the web bundle entirely. Resolution is lazy and memoised — the first log line
 * awaits it, and everything after reuses the same promise.
 */
const noop: LogFn = () => Promise.resolve()

let sinkPromise: Promise<Record<LogLevel, LogFn>> | null = null

function sink(): Promise<Record<LogLevel, LogFn>> {
  if (sinkPromise) return sinkPromise
  sinkPromise = IS_TAURI
    ? import('@tauri-apps/plugin-log').then((m) => ({
        trace: m.trace,
        debug: m.debug,
        info: m.info,
        warn: m.warn,
        error: m.error,
      }))
    : Promise.resolve({
        trace: noop,
        debug: noop,
        info: noop,
        warn: noop,
        error: noop,
      })
  return sinkPromise
}

interface LoggerApi {
  trace: (msg: string, source?: string) => void
  debug: (msg: string, source?: string) => void
  info:  (msg: string, source?: string) => void
  warn:  (msg: string, source?: string) => void
  error: (msg: string, source?: string) => void
}

const SOURCE_PREFIX = 'js:'

function format(msg: string, source?: string): string {
  return source ? `[${SOURCE_PREFIX}${source}] ${msg}` : msg
}

function safeStringify(value: unknown): string {
  if (typeof value === 'string') return value
  if (value instanceof Error) {
    return `${value.name}: ${value.message}\n${value.stack ?? ''}`
  }
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function combine(args: unknown[]): string {
  return args.map(safeStringify).join(' ')
}

const send = (level: LogLevel) => (msg: string, source?: string) => {
  // Fire-and-forget; never let logging failures crash the caller. A rejected
  // sink import is swallowed here for the same reason a rejected write is.
  void sink()
    .then((s) => s[level](format(msg, source)))
    .catch(() => {})
}

export const logger: LoggerApi = {
  trace: send('trace'),
  debug: send('debug'),
  info:  send('info'),
  warn:  send('warn'),
  error: send('error'),
}

/**
 * Dispatch a `stream:debug:error` CustomEvent for the debug-bridge buffer.
 * Wrapped in try/catch so a dispatch failure never escapes the logger.
 */
function dispatchDebugError(detail: {
  source: 'window' | 'unhandledrejection'
  message: string
  stack?: string
}): void {
  try {
    if (typeof window === 'undefined') return
    window.dispatchEvent(
      new CustomEvent('stream:debug:error', {
        detail: {
          ts: new Date().toISOString(),
          source: detail.source,
          message: detail.message,
          ...(detail.stack ? { stack: detail.stack } : {}),
        },
      }),
    )
  } catch {
    // Swallow — logging failures must never propagate.
  }
}

let installed = false

/**
 * Install the global error handlers and console interceptor.
 * Idempotent. Skipped if NODE_ENV === 'test' to avoid interfering with Vitest.
 */
export function installLogger(): void {
  if (installed) return
  // @ts-ignore - process is available in Vitest but not in browser context
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') return
  installed = true

  // ── Console interceptor ─────────────────────────────────────────────────
  const native = {
    log:   console.log.bind(console),
    info:  console.info.bind(console),
    warn:  console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug.bind(console),
  }

  console.log   = (...args: unknown[]) => { native.log(...args);   logger.info(combine(args),  'console.log')  }
  console.info  = (...args: unknown[]) => { native.info(...args);  logger.info(combine(args),  'console.info') }
  console.warn  = (...args: unknown[]) => { native.warn(...args);  logger.warn(combine(args),  'console.warn') }
  console.error = (...args: unknown[]) => { native.error(...args); logger.error(combine(args), 'console.error') }
  console.debug = (...args: unknown[]) => { native.debug(...args); logger.debug(combine(args), 'console.debug') }

  // ── Uncaught synchronous errors ─────────────────────────────────────────
  window.addEventListener('error', (event: ErrorEvent) => {
    const msg = event.error
      ? safeStringify(event.error)
      : `${event.message} (${event.filename}:${event.lineno}:${event.colno})`
    logger.error(`Uncaught: ${msg}`, 'window.onerror')
    // Additive: forward to the debug-bridge captured-error buffer via a
    // generic CustomEvent so the logger does not need a direct dependency
    // on the bridge. See apps/stream/src/shared/debug/error-buffer.ts.
    dispatchDebugError({
      source: 'window',
      message: msg,
      stack: event.error instanceof Error ? event.error.stack : undefined,
    })
  })

  // ── Unhandled promise rejections ────────────────────────────────────────
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const msg = safeStringify(event.reason)
    logger.error(`Unhandled rejection: ${msg}`, 'unhandledrejection')
    dispatchDebugError({
      source: 'unhandledrejection',
      message: msg,
      stack: event.reason instanceof Error ? event.reason.stack : undefined,
    })
  })

  logger.info('Frontend logger installed', 'lib/logger.ts')
}
